import { creatureConditions } from '../../../../shared/rules/creatureAbilities';

const normalizedConditionKeys = (entity) => {
  const sources = [
    entity, entity?.entity, entity?.entityPayload, entity?.characterData, entity?.canonicalData, entity?.data,
    entity?.monsterData, entity?.monsterData?.data, entity?.runtimeOverlay
  ];
  const keys = new Set();
  sources.forEach((source) => {
    if (!source || typeof source !== 'object') return;
    // SRD 5.1 Pack Tactics and adjacent-ally damage require an ally who is
    // not incapacitated, including conditions set by the status controls.
    // date_of_change: 2026-09-05
    creatureConditions(source).forEach((condition) => {
      const key = String(condition?.name ?? condition).trim().toLowerCase().replace(/[\s_-]+/g, '');
      if (key) keys.add(key);
    });
  });
  return keys;
};

export const sameCombatEntity = (left, right) => {
  // Template IDs identify content, not creatures in combat. Two wolves may
  // share a monsterId and still grant each other Pack Tactics. date_of_change: 2026-09-05
  const leftIds = [left?.id, left?.combatantId].filter((value) => value != null && value !== '').map(String);
  const rightIds = new Set([right?.id, right?.combatantId].filter((value) => value != null && value !== '').map(String));
  return leftIds.some((id) => rightIds.has(id));
};

const explicitTeam = (entity) => String(
  entity?.teamId ?? entity?.team ?? entity?.factionId ?? entity?.faction ?? ''
).trim().toLowerCase() || null;

const combatSide = (entity) => {
  const disposition = String(entity?.disposition ?? entity?.allegiance ?? '').trim().toLowerCase();
  if (['player', 'ally', 'allied', 'friendly', 'friend'].includes(disposition)) return 'friendly';
  if (['hostile', 'enemy', 'opponent'].includes(disposition)) return 'hostile';
  if (entity?.isPlayer === true || String(entity?.controller ?? '').toLowerCase() === 'player') return 'friendly';
  const type = String(entity?.type ?? entity?.kind ?? '').trim().toLowerCase();
  if (['character', 'player', 'pc'].includes(type)) return 'friendly';
  if (['monster', 'hostile'].includes(type)) return 'hostile';
  return null;
};

export const areCombatAllies = (left, right) => {
  if (!left || !right) return false;
  if (sameCombatEntity(left, right)) return true;
  const leftTeam = explicitTeam(left);
  const rightTeam = explicitTeam(right);
  if (leftTeam && rightTeam) return leftTeam === rightTeam;
  const leftSide = combatSide(left);
  const rightSide = combatSide(right);
  return Boolean(leftSide && rightSide && leftSide === rightSide);
};

export const areCombatEnemies = (left, right) => {
  if (!left || !right || sameCombatEntity(left, right)) return false;
  const leftTeam = explicitTeam(left);
  const rightTeam = explicitTeam(right);
  if (leftTeam && rightTeam) return leftTeam !== rightTeam;
  const leftSide = combatSide(left);
  const rightSide = combatSide(right);
  return Boolean(leftSide && rightSide && leftSide !== rightSide);
};

export const isCombatantIncapacitated = (entity) => {
  const currentHp = Number(entity?.hpObj?.current ?? entity?.hitPoints?.current ?? entity?.hp);
  const conditions = normalizedConditionKeys(entity);
  return entity?.defeated === true
    || entity?.isDead === true
    || (Number.isFinite(currentHp) && currentHp <= 0)
    || ['incapacitated', 'paralyzed', 'petrified', 'stunned', 'unconscious', 'dead'].some((key) => conditions.has(key));
};
